import { useMutation, useQuery } from '@tanstack/react-query';
import useLocale from 'next-translate/useTranslation';
import nProgress from 'nprogress';
import { useEffect, useState } from 'react';
import { FaFacebook, FaLinkedin, FaReddit, FaXTwitter } from 'react-icons/fa6';
import { toast } from 'sonner';
import { Image } from 'types';

import clsxm from '@/lib/clsxm';

import axiosInstance from '@/utils/httpClient';
import { useUser } from '@/utils/useUser';

import { CopyBtn } from '../atoms/CopyBtn';
import Toggle from '../atoms/Toggle';

interface ShareVideoModalProps {
  video: Partial<Image>;
}

const ShareVideoModal = ({ video }: ShareVideoModalProps) => {
  const [localVideo, setLocalVideo] = useState<Image>(video as Image);
  const [shareWithTeam, setShareWithTeam] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isTeamsPlan } = useUser();
  const { t } = useLocale('chat');

  //useQuery to get the video data
  const query = useQuery(
    ['video', localVideo.id],
    () => {
      setIsLoading(true);

      return axiosInstance.post(`/api/images`, {
        action: 'GET',
        image_id: localVideo.id,
      });
    },
    {
      enabled: false,
      onSuccess: (response) => {
        setIsLoading(false);

        console.log(response.data);

        setLocalVideo(response.data.image);
        setShareWithTeam(Boolean(response.data.image.share_with_team));
        setIsPublic(Boolean(response.data.image.is_public));
      },
    },
  );

  useEffect(() => {
    query.refetch();
  }, []);

  // Use video-specific URL
  const shareLink = `https://easy-peasy.ai/ai-video-generator/videos/${localVideo.slug}`;

  const shareWithTeamMutation = useMutation(
    (vars: { action: string; shareWithTeam?: boolean }) => {
      return axiosInstance.post('/api/images', {
        action: vars.action,
        image_id: localVideo.id,
        ...(vars.shareWithTeam !== undefined && {
          shareWithTeam: vars.shareWithTeam,
        }),
      });
    },
    {
      onSuccess: (response) => {
        if (response.data.image) {
          const { image } = response.data;
          setLocalVideo((prevVideo) => ({ ...prevVideo, ...image }));
        }
        toast.success(response.data.message);
      },
    },
  );

  const handleToggleShareWithTeam = async () => {
    if (Boolean(localVideo?.user_id) && localVideo?.user_id !== user?.id)
      return;
    const nextValue = !shareWithTeam;
    setShareWithTeam(nextValue);
    try {
      nProgress.start();
      await shareWithTeamMutation.mutateAsync({
        action: 'SHARE_WITH_TEAM',
        shareWithTeam: nextValue,
      });
      toast.success(t('Settings saved successfully 👍'));
    } catch (error) {
      console.error(error);
      toast.error('Failed to save settings 😞');
    } finally {
      nProgress.done();
    }
  };

  const handleToggleIsPublic = async () => {
    setIsPublic(!isPublic);
    try {
      nProgress.start();
      await shareWithTeamMutation.mutateAsync({ action: 'TOGGLE_PUBLIC' });
      // toast.success(t('Chat settings saved successfully 👍'));
    } catch (error) {
      console.error(error);
      toast.error('Failed to save 😞');
    } finally {
      nProgress.done();
    }
  };

  return (
    <div className='mx-auto max-w-lg px-10 py-10 sm:px-0'>
      <h2 className='mb-4 text-2xl font-bold'>{t('Share Video')}</h2>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <div className='space-y-6 sm:space-y-5'>
          <div className='sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:pt-5'>
            <label
              htmlFor='share_with_team'
              className='block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2'
            >
              {t('Share Publicly')}
            </label>
            <div className='mt-1 pt-2 sm:col-span-2 sm:mt-0'>
              <Toggle initialValue={isPublic} onChange={handleToggleIsPublic} />
            </div>
          </div>
          <div className={clsxm(!isPublic ? 'hidden' : '', 'mt-10')}>
            <h3 className='text-sm font-medium text-gray-500'>
              {t('Public link to video')}
            </h3>

            <form className='mt-2 flex'>
              <div className='mr-2 w-full'>
                <input
                  type='text'
                  name='email'
                  id='email'
                  className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-0 dark:bg-slate-700/50 dark:text-white sm:text-sm'
                  value={shareLink}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <CopyBtn
                value={shareLink}
                variant='dark'
                disabled={!localVideo.slug}
              />
            </form>
          </div>
          <div
            className={clsxm(
              !isPublic || !localVideo.slug ? 'hidden' : 'flex',
              'mt-6 justify-center space-x-8',
            )}
          >
            <div className='flex flex-col items-center'>
              <a
                href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(
                  shareLink,
                )}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FaLinkedin className='h-8 w-8 text-gray-400 hover:text-[#0077B5]' />
              </a>
              <span className='text-token-text-secondary mt-1 text-xs font-semibold'>
                LinkedIn
              </span>
            </div>
            <div className='flex flex-col items-center'>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  shareLink,
                )}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FaFacebook className='h-8 w-8 text-gray-400 hover:text-[#0866FF]' />
              </a>
              <span className='text-token-text-secondary mt-1 text-xs font-semibold'>
                Facebook
              </span>
            </div>
            <div className='flex flex-col items-center'>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                  shareLink,
                )}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FaXTwitter className='h-8 w-8 text-gray-400 hover:text-[#000000]' />
              </a>
              <span className='text-token-text-secondary mt-1 text-xs font-semibold'>
                X.com
              </span>
            </div>
            <div className='flex flex-col items-center'>
              <a
                href={`https://www.reddit.com/submit?url=${encodeURIComponent(
                  shareLink,
                )}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FaReddit className='h-8 w-8 text-gray-400 hover:text-[#FF4500]' />
              </a>
              <span className='text-token-text-secondary mt-1 text-xs font-semibold'>
                Reddit
              </span>
            </div>
          </div>

          <div className='sm:grid sm:grid-cols-3 sm:items-start sm:gap-4  sm:pt-5'>
            <label
              htmlFor='share_with_team'
              className='block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2'
            >
              {t('Share with Team Members')}
            </label>
            <div className='mt-1 flex items-center pt-2 sm:col-span-2 sm:mt-0'>
              <Toggle
                initialValue={shareWithTeam}
                onChange={handleToggleShareWithTeam}
                disabled={
                  !isTeamsPlan ||
                  (Boolean(localVideo?.user_id) &&
                    localVideo?.user_id !== user?.id)
                }
              />
              {!isTeamsPlan && (
                <p className='ml-5 text-sm text-gray-500'>
                  <a
                    href='/settings/billing-teams'
                    className='text-primary-500 hover:underline'
                    target='_blank'
                  >
                    {t('Upgrade to Teams')}
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareVideoModal;
